import React, { useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';
import config from 'config';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { getExperienceId } from '../acquisition/utils/customizedExperience';
import { LaunchDarklyIdentifier } from '../shared/components/LaunchDarkly/LaunchDarklyIdentifier';
import {
  getLaunchDarklyContext,
  GlobalLDSymbol,
  useLaunchDarklyContextValue,
} from '../shared/utils/launchDarkly';
import {
  loadProfile,
  loadCustomerDetails,
  loadMembership,
} from '../techstyle-shared/react-accounts';
import { getDirectMarketingDisposition } from '../techstyle-shared/react-marketing';
import { getSession, loadSessionDetails } from '../techstyle-shared/redux-core';

export const withLaunchDarklyProvider = (App) => {
  const getInitialProps = App.getInitialProps;

  function WithLDClient(props) {
    return <App {...props} />;
  }

  // Dynamically set up `ctx.ldClient` on the server and client.
  // This is necessary because `getInitialProps` runs on both server and client.
  if (getInitialProps) {
    // On the server, this will set up the server-side SDK.
    if (!process.browser) {
      const { getLaunchDarklyClient } = require('../shared/server/ld-server');
      WithLDClient.getInitialProps = async (pageCtx) => {
        const ctx = 'Component' in pageCtx ? pageCtx.ctx : pageCtx;
        const ldClient = await getLaunchDarklyClient();

        ctx.ldClient = ldClient;

        const initialProps = await getInitialProps(pageCtx);
        return initialProps;
      };
    } else {
      // On the client, this will set up the client-side SDK.
      // This relies on the `LaunchDarklyIdentifier` component to set up the
      // globally available `window[GlobalLDSymbol]` variable.
      WithLDClient.getInitialProps = async (pageCtx) => {
        const ctx = 'Component' in pageCtx ? pageCtx.ctx : pageCtx;
        const ldClient = window[GlobalLDSymbol];
        if (ldClient) {
          ctx.ldClient = ldClient;
        }

        const initialProps = await getInitialProps(pageCtx);
        return initialProps;
      };
    }
  }

  return WithLDClient;
};

const LaunchDarklyProvider = ({ children, flags }) => {
  const ldContextValue = useLaunchDarklyContextValue();

  useEffect(() => {
    Sentry.setContext('launchDarklyContext', ldContextValue);
  }, [ldContextValue]);

  return (
    <LDProvider
      clientSideID={config.get('public.launchDarkly')?.clientKey}
      context={ldContextValue}
      options={{
        privateAttributes: ['email'],
        evaluationReasons: true,
        bootstrap: flags,
      }}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
      flags={flags}
    >
      {children}
    </LDProvider>
  );
};

LaunchDarklyProvider.propTypes = {
  children: PropTypes.node.isRequired,
  flags: PropTypes.object,
};

export default function launchDarklyExtension() {
  return {
    id: 'launchDarkly',
    // Enhancing the `page` on server-side ensures that we have the full LaunchDarkly
    // context available to us during a page's `getInitialProps`. This is necessary when using
    // the `getLDFlagWithCtx` helper function inside of `getInitialProps` (this is abstracted with
    // calls to `getHasFeature` during SSR).
    page() {
      return {
        getInitialProps: {
          enhance(getInitialProps) {
            return async (ctx) => {
              if (typeof window === 'undefined') {
                const initialProps = await getInitialProps(ctx);
                const { store } = ctx;
                const { isLoggedIn } = getSession(ctx.store.getState());

                await Promise.all([
                  store.dispatch(loadProfile()),
                  store.dispatch(loadMembership()),
                  store.dispatch(
                    loadSessionDetails([
                      'gender_select_clicked_mens',
                      'gender_select_clicked_womens',
                    ])
                  ),
                  isLoggedIn
                    ? ctx.store.dispatch(
                        loadCustomerDetails(['signup_source', 'gender'])
                      )
                    : Promise.resolve(),
                ]);

                try {
                  const disposition = getDirectMarketingDisposition(
                    ctx.store.getState()
                  );
                  // This is just making sure the experienceId is set. It should be
                  // happening elsewhere already, so we shouldn't need to pass the
                  // experienceId into the selector and can instead rely on it being
                  // in the redux store.
                  await getExperienceId({
                    isLoggedIn,
                    disposition,
                    dmg: ctx.query?.dmg,
                    gender: ctx.query?.gender,
                    dispatch: ctx.store.dispatch,
                  });
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.log('FAILED TO RETRIEVE DISPOSITION!!!!');
                }

                return initialProps;
              }

              return getInitialProps(ctx);
            };
          },
        },
      };
    },
    app: {
      enhance(App) {
        return withLaunchDarklyProvider(App);
      },
      getInitialProps: {
        enhance(getInitialProps) {
          return async ({ Component, ctx }) => {
            let initialProps = await getInitialProps({ Component, ctx });

            // When running on the server, prefetch all flags for the current user
            // so that they are available on the client. This will ensure that the
            // client will not have to wait for the first request to LaunchDarkly
            // to complete before it can render the page.
            if (typeof window === 'undefined') {
              const { performance } = ctx.req;
              const startTime = process.hrtime();
              const entry = {
                type: 'next',
                name: 'prefetchLaunchDarklyFlags',
                requestStart: performance.getOffset(startTime),
              };
              performance.entries.push(entry);

              const fullLDContext = getLaunchDarklyContext(
                ctx.store.getState()
              );

              const ldFlags = (
                await ctx.ldClient.allFlagsState(fullLDContext, {
                  withReasons: true,
                })
              ).allValues();

              initialProps = {
                ...initialProps,
                ldFlags,
              };

              const endTime = process.hrtime();
              entry.duration = performance.getDuration(startTime, endTime);
            }

            return initialProps;
          };
        },
      },
      render(initialProps, children) {
        const { ldFlags } = initialProps;

        return (
          <LaunchDarklyProvider flags={ldFlags}>
            <LaunchDarklyIdentifier />
            {children}
          </LaunchDarklyProvider>
        );
      },
    },
  };
}
